/**
 * NotificationsOptOutController — section « Préférences email » de
 * /notifications.html (EPIC-033 / 33.5).
 *
 * Hydrate la liste des 18 catégories EmailCategory depuis
 * GET /me/notification-preferences puis pose un toggle par catégorie.
 * Auto-save par toggle : chaque changement déclenche un PUT batch contenant
 * l'état complet courant, avec révert visuel + toast d'erreur en cas
 * d'échec. Le succès est annoncé via un live region `aria-live="polite"`
 * (WCAG 2.1 AA).
 *
 * Catégories `mandatory` : toggle `disabled` + `aria-disabled="true"` +
 * tooltip explicatif. Aucun changement ne peut être envoyé (l'API refuserait
 * de toute façon avec 422 `mandatory_category`).
 *
 * Convention CLAUDE.md respectée : pas d'HTML inline en JS, lignes clonées
 * depuis `<template id="tpl-notifications-opt-out-row">` puis remplies via
 * fillSlots + querySelector ciblé sur l'input et le label.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const MANDATORY_TOOLTIP = "Cette catégorie est obligatoire et ne peut pas être désactivée.";
const STATE_LABEL_ENABLED = "activées";
const STATE_LABEL_DISABLED = "désactivées";

export default class NotificationsOptOutController extends BaseController {
  static targets = ["list", "toast"];

  state = {
    preferences: [],
  };

  async connect() {
    try {
      const data = await api.get("/me/notification-preferences");
      this._hydrate(data);
    } catch (error) {
      console.warn("[notifications-opt-out] hydration failed", error);
      this.showError("Impossible de charger vos préférences email.");
    }
  }

  async onToggle(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const category = input.dataset.category;
    if (!category) return;

    const preference = this.state.preferences.find((p) => p.category === category);
    if (!preference || preference.mandatory) {
      input.checked = !preference?.optedOut;
      return;
    }

    const previousOptedOut = preference.optedOut;
    preference.optedOut = !input.checked;
    this._refreshRowLabel(input, preference);

    const payload = {
      preferences: this.state.preferences
        .filter((p) => !p.mandatory)
        .map((p) => ({ category: p.category, optedOut: p.optedOut })),
    };

    try {
      const data = await api.put("/me/notification-preferences", payload);
      this._hydrate(data);
      this._announce(
        preference.optedOut
          ? `Notifications « ${preference.label} » désactivées.`
          : `Notifications « ${preference.label} » activées.`,
      );
    } catch (error) {
      preference.optedOut = previousOptedOut;
      input.checked = !previousOptedOut;
      this._refreshRowLabel(input, preference);
      const msg = error && error.message
        ? error.message
        : "Erreur lors de l'enregistrement de la préférence.";
      this.showError(msg);
    }
  }

  _hydrate(data) {
    if (!data || !Array.isArray(data.preferences)) return;
    this.state.preferences = data.preferences.map((p) => ({
      category: String(p.category),
      label: String(p.label),
      mandatory: Boolean(p.mandatory),
      optedOut: Boolean(p.optedOut),
    }));
    this._renderList();
  }

  _renderList() {
    if (!this.hasListTarget) return;
    while (this.listTarget.firstChild) {
      this.listTarget.removeChild(this.listTarget.firstChild);
    }

    for (const preference of this.state.preferences) {
      const fragment = this.cloneTemplate("tpl-notifications-opt-out-row");
      if (!fragment) continue;

      this.fillSlots(fragment, {
        label: preference.label,
        description: this._descriptionFor(preference),
        stateLabel: preference.optedOut ? STATE_LABEL_DISABLED : STATE_LABEL_ENABLED,
      });

      const input = fragment.querySelector("input[type='checkbox']");
      if (input) {
        input.dataset.category = preference.category;
        input.checked = !preference.optedOut;
        if (preference.mandatory) {
          input.disabled = true;
          input.setAttribute("aria-disabled", "true");
          input.setAttribute("aria-describedby", `opt-out-mandatory-${preference.category}`);
        }
      }

      const label = fragment.querySelector("label");
      if (label && preference.mandatory) {
        label.title = MANDATORY_TOOLTIP;
        label.style.cursor = "not-allowed";
        const helper = document.createElement("span");
        helper.id = `opt-out-mandatory-${preference.category}`;
        helper.className = "sr-only";
        helper.textContent = MANDATORY_TOOLTIP;
        label.appendChild(helper);
      }

      this.listTarget.appendChild(fragment);
    }
  }

  _refreshRowLabel(input, preference) {
    const li = input.closest("li");
    if (!li) return;
    const stateSpan = li.querySelector("[data-slot='stateLabel']");
    if (stateSpan) {
      stateSpan.textContent = preference.optedOut ? STATE_LABEL_DISABLED : STATE_LABEL_ENABLED;
    }
  }

  _descriptionFor(preference) {
    if (preference.mandatory) {
      return "Catégorie obligatoire — toujours envoyée.";
    }
    return "Vous pouvez désactiver cette catégorie à tout moment.";
  }

  _announce(message) {
    if (!this.hasToastTarget) return;
    this.toastTarget.textContent = "";
    window.setTimeout(() => {
      if (this.hasToastTarget) this.toastTarget.textContent = message;
    }, 50);
  }
}
